import { Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityBodyInterface,
  ActivityInterface,
} from '@sport-activities/sdk';

import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { AlertModalComponent } from '~/component/modal/alert/alert.component';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { orValidator } from '~/core/validators/or/or.validator';
import { urlValidator } from '~/core/validators/url/url.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

@Component({
  selector: 'app-organizer-activity-creation-url',
  templateUrl: './url.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageUrlComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  @ViewChild(AlertModalComponent)
  private readonly alertModalComponent: AlertModalComponent;
  protected readonly pageTitleKey = 'seo.organizer.activity_creation.url.title';

  public readonly tipsConfig: OrganizerSharedPageContainerTipConfigInterface[] =
    [
      {
        descriptionKey: 'activity_creation.url.tips.note.description',
        headerKey: 'activity_creation.url.tips.note.header',
        variant: TipsVariantEnum.WARNING,
      },
      {
        descriptionKey: 'activity_creation.url.tips.about.description',
        headerKey: 'activity_creation.url.tips.about.header',
      },
    ];
  public readonly toggleNameEnum = ToggleNameEnum;

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    private readonly locatableService: OrganizerSharedLocatableService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly formControlService: FormControlService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      liveUrl: formGroupValue.url,
      noteForAttendees: formGroupValue.noteForAttendees,
    };
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    return {
      liveUrl: formGroupValue.url || null,
      noteForAttendees: [
        this.locatableService.buildCurrentLocatableBody(
          formGroupValue.noteForAttendees
        ),
      ],
    };
  }

  protected buildValidationConfig(): void {
    const activity = this.activityService.activity;

    this.validationConfig = {
      url: [
        activity.liveUrl || '',
        orValidator([
          urlValidator(),
          Validators.pattern(/^$/),
          noHtmlTagValidator(),
        ]),
      ],
      noteForAttendees: [activity.noteForAttendees || null],
    };
  }

  public closeModal(): void {
    this.loading$.next(false);
    this.alertModalComponent.hide();
  }

  public errorMessages(inputName: string): string[] {
    return this.formControlService.getErrorMessagesAfterTouch(
      inputName,
      this.formGroup
    );
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (formGroupValue.url === '') {
      this.alertModalComponent.show();
      return;
    }

    this.superSubmit(formGroupValue);
  }

  public superSubmit(formGroupValue: FormGroupValueInterface): void {
    super.submit(formGroupValue);
  }
}
